import { useSelector } from 'react-redux';

import { createOrUpdateBorrower } from '@/apiCalls/borrower';
import { getQuickQuote } from '@/apiCalls/matchingEngine';
import { DisplayButtons } from '@/components/DisplayButtons';
import {
    EAddressOwnershipType,
    EBorrowerCitizenshipStatus,
    ELoanUsage,
    EUserFlow,
    EVehicleAssetType,
    selectAddressByIndex,
    selectAnnualKms,
    selectDeposit,
    selectHasChildren,
    selectIsVehicleKnown,
    selectLoanUsage,
    selectMaritalStatus,
    selectNumberOfChildren,
    selectResidencyStatus,
    selectVehicleAssetType,
    selectVehicleNvic,
    selectVisaNumber,
} from '@/context/borrower';
import {
    EDisplays,
    EModals,
    selectHideSellerScreen,
    selectHideVehicleScreen,
    selectIsAssetKnownViaParams,
    selectIsNZ,
    selectPartner,
    selectUserFlow,
    selectVehicleManualEntry,
    setDisplay,
    setModal,
} from '@/context/display';
import { RootState, store } from '@/context/store';
import { useAppDispatch, useAppSelector } from '@/context/storeHooks';
import { selectIsLoggedIn } from '@/context/user';
import { isVisaExpiryDateValid } from '@/utils/checkVisaExpiryDate';
import { isGlassLookupPossible } from '@/utils/isGlassLookupPossible';

interface IBasicDetailsButtonsProps {
    setShowErrors: (value: boolean) => void;
    baseId: string;
}

export function BasicDetailsButtons({
    setShowErrors,
    baseId,
}: IBasicDetailsButtonsProps): JSX.Element {
    // ***** Redux State *****
    const dispatch = useAppDispatch();
    // const gender = useAppSelector(selectGender);
    const address = useSelector((state: RootState) =>
        selectAddressByIndex(state, 0)
    );
    const ownershipType = address?.ownershipType ?? undefined;
    const residencyStatus = useAppSelector(selectResidencyStatus);
    const visaNumber = useAppSelector(selectVisaNumber);
    const haveKids = useAppSelector(selectHasChildren);
    const numberOfKids = useAppSelector(selectNumberOfChildren);
    const hideSellerScreen = useAppSelector(selectHideSellerScreen);
    const hideVehicleScreen = useAppSelector(selectHideVehicleScreen);
    const isVehicleKnown = useAppSelector(selectIsVehicleKnown);
    const isAssetKnownFromParams = useAppSelector(selectIsAssetKnownViaParams);
    const loanUsage = useAppSelector(selectLoanUsage);
    const isLoggedIn = useAppSelector(selectIsLoggedIn);
    const deposit = useAppSelector(selectDeposit);
    const vehicleManualEntry = useAppSelector(selectVehicleManualEntry);
    const { showQuickQuote } = useAppSelector(selectPartner);
    const userFlow = useAppSelector(selectUserFlow);
    const nvic = useAppSelector(selectVehicleNvic);
    const noLender =
        userFlow === EUserFlow.NO_LENDER ||
        userFlow === EUserFlow.NO_LENDER_EARLY_SIGNUP;
    const maritalStatus = useAppSelector(selectMaritalStatus);
    const annualKms = useAppSelector(selectAnnualKms);
    const isNZ = useAppSelector(selectIsNZ);
    const isAssetTypePersonalLoan =
        useAppSelector(selectVehicleAssetType) ===
        EVehicleAssetType.PERSONAL_LOAN;
    const state = store.getState();

    // visa number check
    const isVisaNumberInvalid = visaNumber === undefined || visaNumber === 0;

    // ***** Event Handlers *****
    function handleBackClick(): void {
        if (isAssetTypePersonalLoan) {
            dispatch(setDisplay(EDisplays.QUICK_CALCULATOR));
            return;
        }
        const isNovatedLease =
            state.borrowerReducer.loan.loanUsage === 'NOVATED';
        if (isNovatedLease && isAssetKnownFromParams) {
            dispatch(setDisplay(EDisplays.QUICK_CALCULATOR));
            return;
        }
        if (isNovatedLease && !isAssetKnownFromParams) {
            dispatch(setDisplay(EDisplays.VEHICLE));
            return;
        }
        if (isNovatedLease) {
            dispatch(setDisplay(EDisplays.QUICK_CALCULATOR));
            return;
        }
        const backScreen = getBackScreen();
        dispatch(setDisplay(backScreen));
    }

    async function handleNextClick(): Promise<void> {
        setShowErrors(true);
        if (!isComplete()) return;
        setShowErrors(false);
        await createOrUpdateBorrower();
        if (loanUsage === ELoanUsage.NOVATED) {
            await getQuickQuote();
            const state = store.getState();
            if (state.lenderReducer.lenders?.length === 0) {
                if (!isLoggedIn) {
                    dispatch(setModal(EModals.REGISTER));
                } else {
                    dispatch(setDisplay(EDisplays.INELIGIBLE));
                }
                return;
            }
        }
        const nextScreen = getNextScreen();
        if (loanUsage === ELoanUsage.BUSINESS) {
            if (
                ownershipType !== EAddressOwnershipType.OWNER &&
                (!deposit || (deposit && deposit < 10))
            ) {
                dispatch(setModal(EModals.ADD_DEPOSIT));
                return;
            } else if (!isLoggedIn) {
                dispatch(setModal(EModals.REGISTER));
                return;
            }
        }
        dispatch(setDisplay(nextScreen));
    }

    // ***** Helpers *****
    function isComplete(): boolean {
        if (maritalStatus === undefined) return false;
        // if (gender === undefined) return false;
        if (ownershipType === undefined) return false;
        if (residencyStatus === undefined) return false;
        if (
            residencyStatus === EBorrowerCitizenshipStatus.VISA_HOLDER &&
            !isNZ &&
            (isVisaNumberInvalid || !isVisaExpiryDateValid())
        )
            return false;
        if (loanUsage === ELoanUsage.PERSONAL && haveKids === undefined)
            return false;
        if (
            loanUsage === ELoanUsage.PERSONAL &&
            haveKids &&
            numberOfKids === undefined
        )
            return false;
        if (loanUsage === ELoanUsage.NOVATED && !annualKms) return false;
        if (userFlow === EUserFlow.CAMPAIGN && !nvic) return false;
        return true;
    }

    function getNextScreen(): EDisplays {
        if (loanUsage === ELoanUsage.BUSINESS) {
            return EDisplays.IDENTIFICATION;
        }
        return EDisplays.INCOME;
    }

    function getBackScreen(): EDisplays {
        // Business Use
        if (loanUsage === ELoanUsage.BUSINESS) {
            return EDisplays.BUSINESS;
        }

        if (userFlow === EUserFlow.CAMPAIGN) {
            return EDisplays.CAMPAIGN_LANDING;
        }

        // Consumer Use
        if (isVehicleKnown) {
            if (!hideSellerScreen) {
                return EDisplays.SELLER;
            }
            if (hideVehicleScreen && isAssetKnownFromParams) {
                if (noLender) {
                    return EDisplays.NO_LENDER_HOME;
                }
                return EDisplays.LOAN;
            }

            if (vehicleManualEntry) {
                // User has gone KNOWN VEHICLE -> Do not know rego -> Cannot find it on glass guide -> Manual entry
                return EDisplays.MANUAL_VEHICLE;
            }

            const glassLookupPossible = isGlassLookupPossible();

            if (glassLookupPossible) {
                return EDisplays.VEHICLE;
            }

            return EDisplays.MANUAL_VEHICLE;
        }

        if (noLender) {
            return EDisplays.NO_LENDER_HOME;
        }

        if (showQuickQuote) return EDisplays.LOAN;

        return EDisplays.NO_LENDER_HOME;
    }

    return (
        <DisplayButtons
            idLeftButton={`${baseId}-button-back`}
            idRightButton={`${baseId}-button-next`}
            rightClick={handleNextClick}
            leftClick={handleBackClick}
        />
    );
}
